import { useEffect, useState } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { loyaltyService } from "@/services";
import { useCurrency } from "@/lib/currency";
import {
  Gift, Award, Users, Coins, TrendingDown, Loader2, ArrowUpRight, ArrowDownRight,
} from "lucide-react";

function Kpi({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="bg-card rounded-xl border border-border p-5 shadow-sm" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
        <Icon className={`h-5 w-5 ${color}`} />
      </div>
      <p className="text-2xl font-bold text-foreground mt-3">{value}</p>
    </div>
  );
}

export default function LoyaltyDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { format } = useCurrency();

  useEffect(() => {
    loyaltyService.dashboard().then(setData).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const k = data?.kpis || {};
  const byType = Object.entries(data?.programsByType || {});
  const recent = data?.recentTransactions || [];

  return (
    <div data-testid="loyalty-dashboard-page">
      <PageHeader title="Loyalty Dashboard" subtitle="Intelligent Loyalty. Predictive Rewards. Retention at scale." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi testid="loy-kpi-programs" label="Active Programs" value={`${k.activePrograms ?? 0}/${k.totalPrograms ?? 0}`} icon={Gift} color="text-primary" />
        <Kpi testid="loy-kpi-members" label="Members" value={(k.totalMembers ?? 0).toLocaleString()} icon={Users} color="text-sky-500" />
        <Kpi testid="loy-kpi-active-members" label="Active Members" value={(k.activeMembers ?? 0).toLocaleString()} icon={Award} color="text-emerald-500" />
        <Kpi testid="loy-kpi-redemption" label="Redemption Rate" value={`${k.redemptionRate ?? 0}%`} icon={TrendingDown} color="text-amber-500" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <Kpi testid="loy-kpi-issued" label="Points Issued" value={(k.pointsIssued ?? 0).toLocaleString()} icon={ArrowUpRight} color="text-emerald-500" />
        <Kpi testid="loy-kpi-redeemed" label="Points Redeemed" value={(k.pointsRedeemed ?? 0).toLocaleString()} icon={ArrowDownRight} color="text-rose-500" />
        <Kpi testid="loy-kpi-outstanding" label="Points Outstanding" value={(k.pointsOutstanding ?? 0).toLocaleString()} icon={Coins} color="text-violet-500" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-xl border border-border p-5 shadow-sm" data-testid="loy-programs-by-type">
          <h3 className="font-semibold text-foreground mb-4">Programs by Type</h3>
          {byType.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">No programs yet</p> : (
            <div className="space-y-3">
              {byType.map(([type, count]) => (
                <div key={type}>
                  <div className="flex justify-between text-sm mb-1"><span className="text-foreground">{type}</span><span className="text-muted-foreground font-semibold">{count}</span></div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: `${(count / Math.max(...byType.map((b) => b[1]))) * 100}%` }} /></div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-card rounded-xl border border-border p-5 shadow-sm" data-testid="loy-recent-txns">
          <h3 className="font-semibold text-foreground mb-4">Recent Transactions</h3>
          {recent.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">No transactions yet</p> : (
            <div className="space-y-2.5 max-h-72 overflow-y-auto">
              {recent.map((t) => (
                <div key={t.id} className="flex items-center justify-between text-sm border-b border-border pb-2">
                  <div>
                    <span className="font-medium text-foreground capitalize">{t.type}</span>
                    <span className="text-muted-foreground"> · {t.customerId}</span>
                  </div>
                  <span className={`font-semibold ${t.delta >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                    {t.delta >= 0 ? "+" : ""}{t.balanceType === "cashback" ? format(t.delta) : `${t.delta} ${t.balanceType}`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
